import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import { Folder, ArrowRight, GraduationCap, CalendarCheck, Article, Wrench, MapPin } from "@phosphor-icons/react";

export default function LearnHub() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/super-categories/${slug}/full`)
      .then((r) => setData(r.data))
      .catch(() => setError("not_found"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <PublicHeader />
        <div className="max-w-3xl mx-auto p-16 text-center">
          <Folder size={64} weight="duotone" className="mx-auto mb-4 text-stone-300" />
          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">Hub not found</h1>
          <p className="text-stone-600 mb-6">We couldn't find a hub page with that slug.</p>
          <Link to="/" className="btn-primary">Back home</Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-[#FDFBF7]"><PublicHeader /><div className="p-16 text-stone-500">Loading…</div></div>;
  const c = data.category;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid={`learn-hub-${slug}`}>
      <Seo title={`${c.name} — Puppl`} description={c.description} />
      <PublicHeader />

      {/* Hero */}
      <section className="py-16 px-6 lg:px-12" style={{ background: c.color }}>
        <div className="max-w-7xl mx-auto text-white">
          <p className="text-xs uppercase tracking-wider font-semibold opacity-80 mb-2">Hub</p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-3">{c.name}</h1>
          {c.description && <p className="text-lg opacity-90 max-w-3xl">{c.description}</p>}
        </div>
      </section>

      {/* Services */}
      {data.services?.length > 0 && (
        <Section title="Services" icon={Wrench}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.services.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="card-pp p-5 flex flex-col hover:-translate-y-0.5 transition-transform" data-testid={`hub-service-${s.slug}`}>
                <Wrench size={24} weight="duotone" className="text-[#f38838] mb-2" />
                <div className="font-heading font-semibold text-stone-900 line-clamp-2">{s.title}</div>
                <div className="text-xs text-stone-500 mt-1 mb-4">{s.price} {s.duration && `• ${s.duration}`}</div>
                <div className="text-sm text-[#f38838] font-semibold inline-flex items-center gap-1 mt-auto pt-2 border-t border-stone-100">Learn more <ArrowRight size={12} weight="bold" /></div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* In-person classes */}
      {data.classes?.length > 0 && (
        <Section title="In-person classes" icon={CalendarCheck}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.classes.map((cl) => (
              <Link key={cl.id} to="/courses/in-person" className="card-pp p-5 flex flex-col hover:-translate-y-0.5 transition-transform" data-testid={`hub-class-${cl.id}`}>
                <CalendarCheck size={24} weight="duotone" className="text-[#1b4a76] mb-2" />
                <div className="font-heading font-semibold text-stone-900 mb-2 line-clamp-2">{cl.name}</div>
                <div className="text-xs text-stone-500 inline-flex items-start gap-1 mb-4">
                  <MapPin size={12} weight="duotone" className="shrink-0 mt-0.5" />
                  <span className="line-clamp-2">{cl.location}</span>
                </div>
                <div className="text-sm text-[#f38838] font-semibold inline-flex items-center gap-1 mt-auto pt-2 border-t border-stone-100">View dates <ArrowRight size={12} weight="bold" /></div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Online courses */}
      {data.courses?.length > 0 && (
        <Section title="Online courses" icon={GraduationCap}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.courses.map((co) => (
              <Link key={co.id} to="/courses/online" className="card-pp overflow-hidden hover:-translate-y-0.5 transition-transform" data-testid={`hub-course-${co.id}`}>
                {co.cover_image && <div className="aspect-[16/10] overflow-hidden"><img loading="lazy" src={co.cover_image} alt={co.title} className="w-full h-full object-cover" /></div>}
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-[#f38838] font-semibold mb-1">{co.level}</div>
                  <div className="font-heading font-semibold text-stone-900">{co.title}</div>
                  <div className="text-xs text-stone-500 mt-1">${co.price}</div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Blog posts */}
      {data.blog_posts?.length > 0 && (
        <Section title="From the blog" icon={Article}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.blog_posts.map((b) => (
              <Link key={b.id} to={`/tips/${b.slug}`} className="card-pp p-5 hover:-translate-y-0.5 transition-transform" data-testid={`hub-blog-${b.slug}`}>
                <Article size={24} weight="duotone" className="text-[#E2B054] mb-2" />
                <div className="font-heading font-semibold text-stone-900">{b.title}</div>
                <div className="text-xs text-stone-500 mt-1 line-clamp-2">{b.excerpt}</div>
                <div className="text-sm text-[#f38838] font-semibold inline-flex items-center gap-1 mt-3">Read more <ArrowRight size={12} weight="bold" /></div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {(data.services?.length || 0) + (data.classes?.length || 0) + (data.courses?.length || 0) + (data.blog_posts?.length || 0) === 0 && (
        <div className="max-w-3xl mx-auto py-20 text-center text-stone-500">
          Nothing has been added to this hub yet — please check back soon.
        </div>
      )}

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="py-12 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-6 inline-flex items-center gap-2">
          <Icon size={24} weight="duotone" className="text-[#f38838]" /> {title}
        </h2>
        {children}
      </div>
    </section>
  );
}
